import { Component } from '@wix/thunderbolt-becky-types';
import { DeviceType } from '@wix/thunderbolt-ssr-api';
import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import {
  DatePickerPlatformProps,
  DatePickerDefinition,
  DatePickerCSSVars,
  DatePickerCssCarmiData,
  DatePickerSkin,
  DatePickerTextAlignment,
} from '../DatePicker.types';
import {
  getInputHeightInPx,
  getLabelPadding,
  getRequiredIndicationDisplay,
} from '../../../core/commons/inputUtils';

export const isMobileViewOrDevice = (
  isMobileView: boolean,
  deviceType: DeviceType,
) => isMobileView || deviceType === 'Smartphone';

export const props = withCompInfo<
  DatePickerPlatformProps,
  DatePickerDefinition
>()(
  [
    'compData',
    'compProps',
    'skin',
    'isMobileView',
    'hasResponsiveLayout',
    'deviceType',
  ],
  ({
    compData,
    compProps,
    skin,
    isMobileView,
    hasResponsiveLayout,
    deviceType,
  }) => {
    const {
      placeholder: dataPlaceholder,
      minDate,
      maxDate,
      allowPastDates,
      allowFutureDates,
      disabledDates,
      disabledDaysOfWeek,
      label,
    } = compData;

    const {
      placeholder: propsPlaceholder,
      defaultTextType,
      isDisabled,
      required,
      readOnly,
      dateFormat,
      dayToStart,
    } = compProps;

    const isMobile = isMobileViewOrDevice(isMobileView, deviceType);

    const normalizedSkin = skin || 'DatePickerDefaultSkin';

    return {
      skin: normalizedSkin,
      label,
      placeholder:
        defaultTextType === 'today'
          ? ''
          : dataPlaceholder || propsPlaceholder || '',
      minDate,
      maxDate,
      allowPastDates,
      allowFutureDates,
      disabledDates,
      disabledDaysOfWeek,
      dateFormat,
      useTodayAsDefaultValue: defaultTextType === 'today',
      weekStartDay: dayToStart === 'Sunday' ? 0 : 1,
      readOnly,
      required,
      isDisabled,
      isCompactMode: isMobile,
      isResponsive: hasResponsiveLayout,
    };
  },
);

const inputWrapperAlignVar = (
  textAlignment: DatePickerDefinition['property']['textAlignment'],
) => {
  switch (textAlignment) {
    case 'right':
      return {
        '--inputWrapperAlign': 'right' as 'right',
      };
    default:
      return {
        '--inputWrapperAlign': 'left' as 'left',
      };
  }
};

const inputPaddingVar = (
  textAlignment: DatePickerDefinition['property']['textAlignment'],
  textPadding: DatePickerDefinition['property']['textPadding'],
) => {
  switch (textAlignment) {
    case 'right':
      return {
        '--inputPadding': `1px ${textPadding}px 1px 2px`,
      };
    default:
      return {
        '--inputPadding': `1px 2px 1px ${textPadding}px`,
      };
  }
};

const calendarButtonPositionVars = (
  textAlignment: DatePickerDefinition['property']['textAlignment'],
) => {
  switch (textAlignment) {
    case 'right':
      return {
        '--calendarButtonLeft': '20px',
        '--calendarButtonRight': 'auto',
      };
    default:
      return {
        '--calendarButtonLeft': 'auto',
        '--calendarButtonRight': '20px',
      };
  }
};

const calendarPositionVars = (
  textAlignment: DatePickerDefinition['property']['textAlignment'],
) => {
  switch (textAlignment) {
    case 'right':
      return {
        '--calendarLeft': 'auto',
        '--calendarRight': '0',
      };
    case 'center':
      return {
        '--calendarLeft': 'calc(50% - 160px)',
        '--calendarRight': 'auto',
      };
    default:
      return {
        '--calendarLeft': '0',
        '--calendarRight': 'auto',
      };
  }
};

const calendarNavRotationVar = (
  textAlignment: DatePickerDefinition['property']['textAlignment'],
) => {
  switch (textAlignment) {
    case 'right':
      return {
        '--calendarNavRotate': '180deg',
      };
    default:
      return {
        '--calendarNavRotate': '0',
      };
  }
};

const calendarNavButtonMarginVars = (
  skin: DatePickerSkin,
  isMobile: boolean,
  textAlignment: DatePickerDefinition['property']['textAlignment'],
) => {
  const getMargins = () => {
    switch (skin) {
      case 'DatePickerTextBetweenNavSkin': {
        if (isMobile) {
          return {
            '--calendarNavYearPrevMargin': [0, -6, -12, 0],
            '--calendarNavYearNextMargin': [-10, 0, 0, -10],
            '--calendarNavMonthPrevMargin': [6, -10, -10, 6],
            '--calendarNavMonthNextMargin': [-10, 54, 54, -10],
          };
        } else {
          return {
            '--calendarNavYearPrevMargin': [],
            '--calendarNavYearNextMargin': [],
            '--calendarNavMonthPrevMargin': [25, 0, 0, 25],
            '--calendarNavMonthNextMargin': [0, 20, 20, 0],
          };
        }
      }
      case 'DatePickerTextYearNavSkin': {
        if (isMobile) {
          return {
            '--calendarNavYearPrevMargin': [],
            '--calendarNavYearNextMargin': [],
            '--calendarNavMonthPrevMargin': [12, -10, -10, 12],
            '--calendarNavMonthNextMargin': [-10, 12, 12, -10],
          };
        } else {
          return {
            '--calendarNavYearPrevMargin': [],
            '--calendarNavYearNextMargin': [],
            '--calendarNavMonthPrevMargin': [10, 0, 0, 10],
            '--calendarNavMonthNextMargin': [0, 10, 10, 0],
          };
        }
      }
      default: {
        if (isMobile) {
          return {
            '--calendarNavYearPrevMargin': [],
            '--calendarNavYearNextMargin': [],
            '--calendarNavMonthPrevMargin': [],
            '--calendarNavMonthNextMargin': [],
          };
        } else {
          return {
            '--calendarNavYearPrevMargin': [],
            '--calendarNavYearNextMargin': [],
            '--calendarNavMonthPrevMargin': [5, 0, 5, 0],
            '--calendarNavMonthNextMargin': [0, 5, 0, 5],
          };
        }
      }
    }
  };
  const isRtl = textAlignment === 'right';
  return Object.entries(getMargins()).reduce(
    (margins, [varName, [left, right, rtlLeft, rtlRight]]) => {
      return {
        ...margins,
        [varName]: isRtl
          ? `0 ${rtlRight || 0}px 0 ${rtlLeft || 0}px`
          : `0 ${right || 0}px 0 ${left || 0}px`,
      };
    },
    {},
  ) as {
    '--calendarNavYearPrevMargin': string;
    '--calendarNavYearNextMargin': string;
    '--calendarNavMonthPrevMargin': string;
    '--calendarNavMonthNextMargin': string;
  };
};

const editorSpecificStyling = ({
  compProps,
  compLayout,
  isMobileView,
  hasResponsiveLayout,
  textAlignment,
}: {
  compProps: DatePickerDefinition['property'];
  compLayout: Component['layout'];
  isMobileView: boolean;
  hasResponsiveLayout: boolean;
  textAlignment: DatePickerTextAlignment;
}) => {
  if (hasResponsiveLayout) {
    return {
      '--inputWrapperAlignItems':
        textAlignment === 'right'
          ? ('flex-end' as const)
          : ('flex-start' as const),
    };
  } else {
    return {
      '--inputWrapperHeight': getInputHeightInPx({
        compProps,
        compLayout,
        isMobileView,
      }),
      height: 'auto' as 'auto',
    };
  }
};

export const getCss = (
  {
    skin,
    compData,
    compProps,
    compLayout,
    styleProperties,
    isMobileView,
    hasResponsiveLayout,
    deviceType,
  }: {
    skin: DatePickerSkin;
    compData: DatePickerDefinition['data'];
    compProps: DatePickerDefinition['property'];
    compLayout: Component['layout'];
    styleProperties: Record<string, string>;
    isMobileView: boolean;
    hasResponsiveLayout: boolean;
    deviceType: DeviceType;
  },
  cssVars: DatePickerCssCarmiData,
): DatePickerCSSVars => {
  const { label } = compData;
  const { textAlignment, textPadding, labelMargin } = compProps;

  const isMobile = isMobileViewOrDevice(isMobileView, deviceType);

  return {
    ...cssVars,
    '--textAlign': textAlignment,
    '--dir': (textAlignment === 'right' ? 'rtl' : 'ltr') as 'ltr' | 'rtl',
    '--labelMarginBottom': `${labelMargin}px`,
    '--labelPadding': getLabelPadding(compProps) || 'auto',
    '--requiredIndicationDisplay': getRequiredIndicationDisplay(
      styleProperties,
    ),
    ...inputWrapperAlignVar(textAlignment),
    ...inputPaddingVar(textAlignment, textPadding),
    ...calendarButtonPositionVars(textAlignment),
    ...calendarPositionVars(textAlignment),
    ...calendarNavRotationVar(textAlignment),
    '--calendarMarginTop': isMobile ? '0' : '22px',
    '--labelDisplay': !label ? 'none' : 'inline-block',
    ...calendarNavButtonMarginVars(skin, isMobile, textAlignment),
    ...editorSpecificStyling({
      compProps,
      compLayout,
      isMobileView,
      hasResponsiveLayout,
      textAlignment,
    }),
  };
};

export const css = withCompInfo<
  DatePickerCSSVars,
  DatePickerDefinition,
  DatePickerCssCarmiData
>()(
  [
    'skin',
    'compData',
    'compProps',
    'compLayout',
    'styleProperties',
    'isMobileView',
    'hasResponsiveLayout',
    'deviceType',
  ],
  (
    {
      skin,
      compData,
      compProps,
      compLayout,
      styleProperties,
      isMobileView,
      hasResponsiveLayout,
      deviceType,
    },
    cssVars,
  ) =>
    getCss(
      {
        skin,
        compData,
        compProps,
        compLayout,
        styleProperties,
        isMobileView,
        hasResponsiveLayout,
        deviceType,
      },
      cssVars,
    ),
);

export default createComponentMapperModel({ props, css });
